
import React, { useState } from 'react';
import { LogType } from '../types';

interface NewLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { logType: LogType, location: string, text: string, tags: string[] }) => void;
}

const NewLogModal: React.FC<NewLogModalProps> = ({ onClose, onSave }) => {
  const [logType, setLogType] = useState<LogType>(LogType.ISSUE);
  const [location, setLocation] = useState('');
  const [text, setText] = useState('');

  const templates = [
    "Owner's rep requested...",
    "Discovered uncharted line...",
    "Design conflict found...",
  ];

  return (
    <div className="fixed inset-0 bg-white/80 backdrop-blur-md flex items-center justify-center z-50 p-6">
      <div className="bg-white rounded-[2rem] border border-slate-100 shadow-2xl w-full max-w-xl overflow-hidden flex flex-col max-h-[85vh]">
        <div className="px-10 py-8 flex justify-between items-center border-b border-slate-50">
          <h2 className="text-xl font-light tracking-tight text-slate-900">New Field Entry</h2>
          <button onClick={onClose} className="text-slate-300 hover:text-black transition-colors p-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="px-10 py-8 space-y-8 overflow-y-auto">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label className="block text-[8px] font-black text-slate-300 uppercase tracking-widest">Category</label>
              <select 
                value={logType}
                onChange={(e) => setLogType(e.target.value as LogType)}
                className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 text-xs focus:ring-1 focus:ring-slate-200 outline-none appearance-none cursor-pointer"
              >
                {Object.values(LogType).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-[8px] font-black text-slate-300 uppercase tracking-widest">Location</label>
              <input 
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Area B, L2"
                className="w-full bg-slate-50 border-none rounded-xl px-4 py-3 text-xs focus:ring-1 focus:ring-slate-200 outline-none"
              />
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-[8px] font-black text-slate-300 uppercase tracking-widest">Quick Start</p>
            <div className="flex flex-wrap gap-2">
              {templates.map(tpl => (
                <button 
                  key={tpl}
                  onClick={() => setText(tpl)}
                  className="text-[9px] bg-slate-50 hover:bg-slate-100 text-slate-500 px-3 py-1.5 rounded-lg transition-colors border border-slate-100 font-medium"
                >
                  {tpl}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="block text-[8px] font-black text-slate-300 uppercase tracking-widest">Narrative</label>
            <textarea 
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              placeholder="Detail for AI audit..."
              className="w-full bg-slate-50 border-none rounded-xl px-4 py-4 text-xs focus:ring-1 focus:ring-slate-200 outline-none resize-none placeholder:text-slate-300 leading-relaxed"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
             <button className="flex flex-col items-center justify-center gap-1.5 py-6 bg-slate-50 rounded-xl text-slate-400 hover:bg-slate-100 transition-all border border-dashed border-slate-200">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                <span className="text-[8px] font-black uppercase tracking-widest">Evidence</span>
             </button>
             <button className="flex flex-col items-center justify-center gap-1.5 py-6 bg-slate-50 rounded-xl text-slate-400 hover:bg-slate-100 transition-all border border-dashed border-slate-200">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
                <span className="text-[8px] font-black uppercase tracking-widest">Voice</span>
             </button>
          </div>
        </div>

        <div className="px-10 py-8 bg-white border-t border-slate-50 flex gap-4">
          <button onClick={onClose} className="flex-1 py-4 text-slate-400 text-[10px] font-black uppercase tracking-widest hover:text-slate-600 transition-colors">Discard</button>
          <button 
            onClick={() => onSave({ logType, location, text, tags: [] })}
            className="flex-1 py-4 bg-black text-white text-[10px] font-black uppercase tracking-[0.2em] rounded-2xl shadow-xl hover:bg-slate-800 transition-all"
          >
            Commit
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewLogModal;
